'use client';

import * as React from 'react';

import { ArrowRight, CalendarDays, Check, HelpCircle } from 'lucide-react';

import { useModal } from '@/components/modals/modal-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { PricingTier } from '@/lib/pricing';
import { cn } from '@/lib/utils';

function ControlledTooltip({ children, ...props }: React.ComponentPropsWithoutRef<typeof Tooltip>) {
  const [open, setOpen] = React.useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen} {...props}>
      {children}
    </Tooltip>
  );
}

export function PricingCard({ tier }: { tier: PricingTier }) {
  const { openModal } = useModal();
  const isDisabled = tier.comingSoon;

  return (
    <Card
      className={cn(
        'relative mt-4 flex min-h-[580px] w-full max-w-[360px] flex-col transition-all hover:border-muted-foreground/50 hover:shadow-lg active:border-muted-foreground/50 active:shadow-lg lg:flex-[0_0_32%] dark:hover:border-muted-foreground dark:active:border-muted-foreground',
        tier.popular && 'border-primary hover:border-primary active:border-primary',
        isDisabled && 'opacity-80',
      )}
    >
      {tier.popular && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
            Most Popular
          </span>
        </div>
      )}

      {tier.comingSoon && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
            Coming Soon
          </span>
        </div>
      )}

      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl leading-tight">
          {tier.icon}
          {tier.name}
        </CardTitle>
        <p className="mt-2 h-12 leading-snug text-foreground">{tier.description}</p>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col">
        <div className="flex h-[200px] flex-col space-y-5 border-b pb-6">
          <div className="flex-1">
            {tier.price ? (
              <>
                <div className="text-[2.75rem] tabular-nums">{tier.price}</div>
                <TooltipProvider>
                  <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                    <span>{tier.priceLabel}</span>
                    {tier.priceHint && (
                      <ControlledTooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="More information about pricing"
                            className="cursor-help text-muted-foreground transition-colors hover:text-foreground"
                          >
                            <HelpCircle className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          align="start"
                          className="max-w-xs rounded-lg border bg-card px-4 py-2.5 text-sm text-card-foreground shadow-lg"
                        >
                          <p>{tier.priceHint}</p>
                        </TooltipContent>
                      </ControlledTooltip>
                    )}
                  </div>
                </TooltipProvider>
              </>
            ) : (
              <div className="text-3xl">{tier.priceLabel || 'Custom Pricing'}</div>
            )}
          </div>

          {(() => {
            const cta = tier.cta;
            if ('action' in cta) {
              return (
                <Button
                  size="lg"
                  variant={tier.popular ? 'default' : 'outline'}
                  disabled={isDisabled}
                  className="w-full cursor-pointer font-semibold"
                  onClick={() =>
                    openModal(cta.action, `pricing_${tier.name.toLowerCase().replace(/\s+/g, '_')}`)
                  }
                >
                  {cta.text}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              );
            }
            return (
              <Button
                size="lg"
                variant={tier.popular ? 'default' : 'outline'}
                disabled={isDisabled}
                className="w-full cursor-pointer font-semibold"
                asChild={!isDisabled}
              >
                {isDisabled ? (
                  <span>{cta.text}</span>
                ) : (
                  <a
                    href={cta.href}
                    target={cta.external ? '_blank' : undefined}
                    rel={cta.external ? 'noopener noreferrer' : undefined}
                  >
                    <CalendarDays className="mr-1 h-4 w-4" />
                    {cta.text}
                  </a>
                )}
              </Button>
            );
          })()}
        </div>

        <TooltipProvider>
          <ul className="space-y-3 pt-6 text-sm">
            {tier.includesPreviousTier && (
              <li className="mb-4 font-medium">
                <span>Everything in {tier.includesPreviousTier} +</span>
              </li>
            )}
            {tier.features.map((feature, index) => {
              const isFeatureObject = typeof feature === 'object' && feature !== null;
              const text = isFeatureObject ? feature.text : feature;
              const tooltip = isFeatureObject ? feature.tooltip : null;

              return (
                <li key={index} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                  {tooltip ? (
                    <ControlledTooltip delayDuration={50}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={`More information about ${text}`}
                          className="flex-1 cursor-help text-left text-muted-foreground underline decoration-dotted underline-offset-4"
                        >
                          {text}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="start"
                        className="max-w-xs rounded-lg border bg-card px-4 py-2.5 text-sm text-card-foreground shadow-lg"
                      >
                        <p>{tooltip}</p>
                      </TooltipContent>
                    </ControlledTooltip>
                  ) : (
                    <span className="flex-1 text-muted-foreground">{text}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}
